import React from "react";
import { Button, Box, Card, CardContent, CardHeader, Popover, WithStyles, FormControlLabel, Checkbox, CircularProgress } from "@material-ui/core";
import { FieldArray, FieldArrayRenderProps, FormikProps } from "formik";
import { B2bCompany, B2bCompanyProduct } from "../../../../model/B2bCompany";
import EditB2bCompanyCustomProduct from "./EditB2bCompanyCustomProduct";
import { ApiBackend } from "../../../../providers/apibackend";
import { Product } from "../../../../model/Product";
import SelectCompanyProductPopover from "../Popovers/SelectCompanyProductPopover";
import { CompanyReservationCode } from "../../../../model/CompanyReservationCode";

interface IState {
	popupOpen: boolean;
	anchorEl: HTMLButtonElement;
	sourceProducts: Product[];
	codes: CompanyReservationCode[];
	includeInactive: boolean;
	loading: boolean;
}

class EditB2bCompanyCustomProducts extends React.Component<WithStyles & FormikProps<B2bCompany>, IState> {
	private readonly api: ApiBackend;

	constructor(props: WithStyles & FormikProps<B2bCompany>) {
		super(props);
		this.state = {
			popupOpen: false,
			anchorEl: undefined,
			sourceProducts: [],
			codes: [],
			includeInactive: false,
			loading: true
		};
		this.api = new ApiBackend();
	}

	async componentDidMount(): Promise<void> {
		this.updateAllSourceProductsAndReservations();
	}

	render(): JSX.Element {
		const { popupOpen, anchorEl, includeInactive, loading } = this.state;
		const { classes } = this.props;
		const { products } = this.props.values;

		const aggregationGroupValues = [...products.map(x => x.aggregationGroup).filter(Boolean).sort()];
		const uniqueValues = aggregationGroupValues.reduce((acc, curr) => {
			if (!acc.includes(curr))
				acc.push(curr);
			return acc;
		}, []);

		const defaultBoxStyle = {
			display: "flex",
			flexDirection: "row",
		};

		const selectableCodes = this.state.codes?.filter(x => x.codeHolderName.indexOf("B2B:") === -1);

		return (this.state.sourceProducts &&
			<Card>
				<FieldArray
					name="products"
					render={arrayHelpers => (
						<>
							<CardHeader
								className={classes.cardHeader}
								title="Hantera produkter"
								action={
									<Box {...defaultBoxStyle} style={{ gap: "16px", marginLeft: "auto", marginTop: 8, marginRight: 8 }}>
										<FormControlLabel
											label="Visa även inaktiva produkter"
											control={
												<Checkbox style={{ color: "#fff" }}
													checked={includeInactive}
													onChange={(ev) => {
														this.setState({ includeInactive: ev.target.checked });
													}}
												/>
											}
										/>
										<Button color="primary" variant="text"
											onClick={this.handleAddProduct}
										>
											Lägg till produkt
										</Button>
										<Popover
											open={popupOpen}
											anchorEl={anchorEl}
											onClose={this.handleAddProductClose}
											anchorOrigin={{
												vertical: 'top',
												horizontal: 'center',
											}}
											transformOrigin={{
												vertical: 'bottom',
												horizontal: 'center',
											}}
											PaperProps={{
												style: {
													width: "500px"
												}
											}}>
											<SelectCompanyProductPopover
												existingCompanyProducts={products}
												selectableCodes={selectableCodes}
												isLegacy={false}
												onChange={(product) => this.handleOnPopoverChange(product, arrayHelpers)}
											/>
										</Popover>
									</Box>
								}
							/>
							<CardContent>
								{loading &&
									<CircularProgress color="secondary" />
								}
								{products.map((product, idx) => {
									const sourceProduct = this.state.sourceProducts.find((sourceProduct) => sourceProduct.id === product.productId);
									const showItem = includeInactive || !includeInactive && product.isActive;
									return sourceProduct && showItem && <EditB2bCompanyCustomProduct {...this.props}
										key={idx}
										idx={idx}
										product={product}
										sourceProduct={sourceProduct}
										codes={this.state.codes}
										aggregationGroupValues={uniqueValues}
										arrayHelpers={arrayHelpers}
										onDelete={(idx: number) => this.handleOnDeleteProduct(idx, arrayHelpers)}
										onSave={(product: B2bCompanyProduct) => this.handleOnSaveProduct(idx, product, arrayHelpers)}
									/>
								})}
							</CardContent>
						</>
					)} />
			</Card>
		)
	}

	private handleOnDeleteProduct = (idx: number, arrayHelpers: FieldArrayRenderProps) => {
		arrayHelpers.remove(idx);
	};

	private handleOnSaveProduct = (idx: number, product: B2bCompanyProduct, arrayHelpers: FieldArrayRenderProps) => {
		arrayHelpers.replace(idx, product);
	}

	private handleAddProduct = (e: any) => {
		this.setState({ popupOpen: true, anchorEl: e.currentTarget });
	};

	private handleAddProductClose = () => {
		this.setState({ popupOpen: false, anchorEl: undefined });
	};

	private handleOnPopoverChange = async (product: B2bCompanyProduct, arrayHelpers: FieldArrayRenderProps) => {
		const sourceProductExists = this.state.sourceProducts.some((p) => p.id == product.productId);
		if (!sourceProductExists) {
			await this.addSourceProduct(product.productId);
		}

		this.handleAddProductClose();
		arrayHelpers.push(product);
	};

	private updateAllSourceProductsAndReservations = async (): Promise<void> => {
		const sourceProductIds = this.props.values.products.map((product) => product.productId);
		const uniqueProductIds = sourceProductIds.filter((v, i, a) => a.indexOf(v) === i);
		const uniqueSourceProducts: Product[] = await Promise.all(uniqueProductIds.map(async (productId): Promise<Product> => {
			return await this.api.getProduct(productId);
		}));

		let nextCodes: CompanyReservationCode[] = [];
		const codesResponse = await this.api.listCompanyCodes(this.props.values.id);
		if (codesResponse.status === "OK" && codesResponse.codes) {
			nextCodes = codesResponse.codes;
		}

		this.setState({
			sourceProducts: uniqueSourceProducts,
			codes: nextCodes,
			loading: false
		});
	}

	private addSourceProduct = async (productId: string): Promise<void> => {
		const newSourceProduct = await this.api.getProduct(productId);
		this.setState(prevState => {
			return {
				...prevState,
				sourceProducts: [
					...prevState.sourceProducts,
					newSourceProduct
				]
			}
		});
	}
}

export default EditB2bCompanyCustomProducts;